import { useEffect, useSyncExternalStore } from 'react';
import { taskChatOrigin } from './task-origin';

/**
 * Where a send from the sticky "Ask {maestro}" bar goes when the screen is ABOUT something with its own
 * chat (the task page): into `conversationId` when it has one, else a fresh chat carrying `taskId`.
 * Web twin of iOS `StickyChatTarget` / Android `StickyChatTarget`.
 *
 * A store rather than a context for the same reason as `sticky-chat-slot`: the bar is mounted once for
 * the whole app and is not a descendant of the screen that sets it.
 */
export interface StickyChatTarget {
  conversationId?: string;
  taskId?: string;
}

let target: StickyChatTarget | null = null;
const listeners = new Set<() => void>();

function set(next: StickyChatTarget | null): void {
  target = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const read = () => target;
const readServer = () => null;

/** Points the bar's sends at this screen's chat for as long as the screen is mounted. */
export function useRegisterStickyChatTarget(resolveConversationId: string | undefined, taskId: string | undefined): void {
  const conversationId = taskChatOrigin({ resolveConversationId })?.conversationId;
  useEffect(() => {
    if (!conversationId && !taskId) return undefined;
    const mine: StickyChatTarget = { conversationId, taskId: taskId || undefined };
    set(mine);
    return () => {
      if (target === mine) set(null);
    };
  }, [conversationId, taskId]);
}

/** The registered target, or `null` when sends start a fresh chat. */
export function useStickyChatTarget(): StickyChatTarget | null {
  return useSyncExternalStore(subscribe, read, readServer);
}
